import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import API from "../../services/API";
import { TokenService } from "../../services/LocalStorage";
import { RootState } from "../store";

interface Faq {
  faqID: number;
  answer: string;
  faqDate: string;
  question: string;
  yesCount: number;
  noCount: number;
  attachments?: attachments[];
}

interface attachments {
  AttachmentName: string;
  AttachmentRoute: string;
}

interface FaqState {
  faqDestacados: Faq[];
  list: Faq[];
  active: Faq;
  faqsFounded: Faq[];
  faqsFoundedByDesc: Faq[];
  page: number;
  pageData: Faq[];
  pagesTotal: number;
  rowsPerPage: number,
  totalRecords: number;
}

const initialState: FaqState = {
  list: [],
  faqDestacados: [],
  active: {
    faqID: 0,
    answer: "",
    faqDate: "",
    question: "",
    yesCount: 0,
    noCount: 0,
  },
  faqsFounded: [],
  faqsFoundedByDesc: [],
  page: 0,
  pageData: [],
  pagesTotal: 10,
  rowsPerPage: 10,
  totalRecords: 10, 
};

export const FaqStore = createSlice({
  name: "faq",
  initialState,
  reducers: {
    setFaq: (state: { list: Faq[] }, action: PayloadAction<Faq[]>) => {
      state.list = action.payload;
    },
    setFaqDestacados: (
      state: { faqDestacados: Faq[] },
      action: PayloadAction<Faq[]>
    ) => {
      state.faqDestacados = action.payload;
    },
    setActiveFaq: (state: { active: Faq }, action: PayloadAction<Faq>) => {
      state.active = action.payload;
    },
    setFoundedFaq: (
      state: { faqsFounded: Faq[] },
      action: PayloadAction<Faq[]>
    ) => {
      state.faqsFounded = action.payload;
    },
    setFoundedFaqExt: (
      state: { faqsFoundedByDesc: Faq[] },
      action: PayloadAction<Faq[]>
    ) => {
      state.faqsFoundedByDesc = action.payload;
    },
    setFilteredFaq: (state: { list: any[] }, action: PayloadAction<string>) => {
      state.list = state.list.filter((item: Faq) => {
        return (
          item.question.toLowerCase().indexOf(action.payload.toLowerCase()) !==
          -1
        );
      });
    },
    setFilteredFaqSearch(state: { list: Faq[] }, action: PayloadAction<string>) {
      state.list = initialState.list;
    },
    setRowsPerPage: (
      state: { rowsPerPage: number },
      action: PayloadAction<number>
    ) => {
      state.rowsPerPage = action.payload;
    },
    setCurrentPageFaqData: (
      state: { list: any[], pageData: any, rowsPerPage: number },
      action: PayloadAction<number>
    ) => {
      const startIndex = (action.payload - 1) * state.rowsPerPage;
      const endIndex = startIndex + state.rowsPerPage;
      const newData = state.list.slice(startIndex, endIndex);
      state.pageData = newData;
    },
    setCurrentPageFaqDataSearch: (
      state: { list: any[], pageData: any, rowsPerPage: number },
      action: PayloadAction<number>
    ) => {
      const newData = state.list.slice(action.payload, action.payload + state.rowsPerPage);
      state.pageData = newData;
    },
    setTotalPages: (
      state: { pagesTotal: number },
      action: PayloadAction<number>
    ) => {
      state.pagesTotal = action.payload;
    },
    setTotalRecords: (
      state: { totalRecords: number },
      action: PayloadAction<number>
    ) => {
      state.totalRecords = action.payload;
    },
    cleanStore: () => {
      return { ...initialState };
    },
  },
});

export const getFaq = (page: number, rowsPerPage: number) => async (dispatch: any) => {
  try {
    let userlogin: string = localStorage.getItem('userName') ?? '';

    if (userlogin !== '') {
      userlogin = userlogin.trim().replaceAll('"', '');
    }

    const payload: any = {
      faqUserRequester: userlogin,
      maxRequieredRegistersLimit: 1000,
      pageSize: page,
    };

    API()
      .post('selfservice/faq', payload)
      .then((response) => {
        let data: Faq[] = [];
        response.data.Values.forEach((faqItem: any) => {
          data.push({
            faqID: faqItem.FaqId,
            answer: faqItem.FaqAnswer,
            faqDate: faqItem.FaqDate,
            question: faqItem.FaqQuestion,
            yesCount: faqItem.AcceptedRate,
            noCount: faqItem.RejectedRate,
            attachments: faqItem.Attachments
          });
        });

        data = data
          .sort((a, b) => {
            return a.yesCount - b.yesCount;
          })
          .reverse();
        let offset = 0;
        if (page) offset = page;
        dispatch(setFaq(data));
        dispatch(setTotalPages(Math.ceil(data.length )));
        dispatch(setCurrentPageFaqData(page));
      });
  } catch (e: any) {
    return console.error(e.message);
  }
};

export const getFaqDestacados = () => async (dispatch: any) => {
  try {
    let userlogin: string = localStorage.getItem('userName') ?? '';

    if (userlogin !== '') {
      userlogin = userlogin.trim().replaceAll('"', '');
    }

    const payload: any = {
      faqUserRequester: userlogin,
      isRequieredByRateOrder: true,
      maxRequieredRegistersLimit: 5
    };

    API()
      .post('selfservice/faq', payload)
      .then((response) => {
        let data: Faq[] = [];
        response.data.Values.forEach((faqItem: any) => {
          data.push({
            faqID: faqItem.FaqId,
            answer: faqItem.FaqAnswer,
            faqDate: faqItem.FaqDate,
            question: faqItem.FaqQuestion,
            yesCount: faqItem.AcceptedRate,
            noCount: faqItem.RejectedRate
          });
        });

        dispatch(setFaqDestacados(data));
      });
  } catch (e: any) {
    return console.error(e.message);
  }
};

export const getActiveFaq = (faqID: number) => async (dispatch: any) => {
  try {
    API()
      .get(
        `knowledge/getfaq?token=${TokenService.get()}&faqID=${faqID}&StatusID=1`
      )
      .then((response) => {
        dispatch(
          setActiveFaq({
            faqID: response.data.Values[0].FaqID,
            answer: response.data.Values[0].Answer,
            faqDate: response.data.Values[0].FaqDate,
            question: response.data.Values[0].Question,
            yesCount: response.data.Values[0].CountAccepted,
            noCount: response.data.Values[0].CountNoAccepted,
            attachments: response.data.Values[0].Attachments || []
          })
        );
      });
  } catch (e: any) {
    return console.error(e.message);
  }
};

export const searchFaq = (
  searchTerm: string,
  page?: number,
  isRequieredFullFaqContent: boolean = false
) => async (dispatch: any) => {
  if (!searchTerm || searchTerm.trim() === '') return;

  try {
    const userlogin: string =
      (localStorage.userName && JSON.parse(localStorage.userName)) || '';

    const payload: any = {
      faqUserRequester: userlogin,
      isRequieredByRateOrder: true,
      isRequieredFullFaqContent: isRequieredFullFaqContent,
      maxRequieredRegistersLimit: 10,
      searchedFaq: searchTerm,
    };

    const response = await API().post('selfservice/faq', payload);

    const data: Faq[] = response.data.Values.map((faqItem: any) => ({
      faqID: faqItem.FaqId,
      answer: faqItem.FaqAnswer,
      faqDate: faqItem.FaqDate,
      question: faqItem.FaqQuestion,
      yesCount: faqItem.AcceptedRate,
      noCount: faqItem.RejectedRate,
    }));

    const offset = page || 0;

    dispatch(setFaq(data));
    dispatch(setTotalPages(data.length));
    dispatch(setCurrentPageFaqDataSearch(offset));
    dispatch(setFoundedFaq(data));
  } catch (e: any) {
    console.error('Error en searchFaq:', e.message);
  }
};


export const searchFaqByDesc = (term: string) => async (dispatch: any) => {
  try {
    API()
      .get(
        `knowledge/getfaq?token=${TokenService.get()}&isPrivate=false&StatusID=1&content=${term}`
      )
      .then((response) => {
        let data: Faq[] = [];

        if (term !== "") {
          response.data.Values.forEach((faqItem: any) => {
            data.push({
              faqID: faqItem.FaqID,
              answer: faqItem.Answer,
              faqDate: faqItem.FaqDate,
              question: faqItem.Question,
              yesCount: faqItem.CountAccepted,
              noCount: faqItem.CountNoAccepted,
            });
          });
        }

        data = data
          .sort((a, b) => {
            return a.yesCount - b.yesCount;
          })
          .reverse();
        dispatch(setFoundedFaqExt(data));
      });
  } catch (e: any) {
    return console.error(e.message);
  }
};
export const rateArticle =
  (faqID: number, isUseful: boolean, observations: string) => async () => {
    try {
      API()
        .post(`knowledge/RateKnowledgeManagment`, {
          faqCode: faqID,
          isUseful: isUseful,
          observations: observations,
          token: TokenService.get(),
        })
        .then(() => { });
    } catch (error) {
      console.error(error);
    }
  };

export const filterFaq = (term: string) => async (dispatch: any) => {
  await dispatch(setFilteredFaq(term));
};

export const clearFaq = () => async (dispatch: any) => {
  await dispatch(setFoundedFaq([]));
  await dispatch(setFoundedFaqExt([]));
};

export const {
  setFaq,
  setFilteredFaq,
  setActiveFaq,
  setFoundedFaq,
  setFoundedFaqExt,
  setCurrentPageFaqData,
  setCurrentPageFaqDataSearch,
  setTotalPages,
  setTotalRecords,
  setFaqDestacados,
  cleanStore,
  setRowsPerPage
} = FaqStore.actions;
export const faq = (state: RootState) => state.faq;
export default FaqStore.reducer;
